"use client";

/**
 * useCompareShortlist: manages the comparison shortlist.
 *
 * State is kept in:
 *   1. React state (in-memory, drives renders)
 *   2. localStorage key `costdoc_compare` (survives page refresh)
 *   3. URL param `?compare=slug1,slug2` (shareable link)
 *
 * Max 4 providers: matches the spec and keeps the modal table readable.
 */

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "costdoc_compare";
const MAX_PROVIDERS = 4;
const URL_PARAM = "compare";

function readFromStorage(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s): s is string => typeof s === "string").slice(0, MAX_PROVIDERS);
  } catch {
    return [];
  }
}

function persistToStorage(slugs: string[]): void {
  try {
    if (slugs.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(slugs));
    }
  } catch {
    // Silently swallow quota errors
  }
}

function syncUrlParam(slugs: string[]): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  if (slugs.length > 0) {
    params.set(URL_PARAM, slugs.join(","));
  } else {
    params.delete(URL_PARAM);
  }
  const newSearch = params.toString();
  const newUrl = newSearch
    ? `${window.location.pathname}?${newSearch}`
    : window.location.pathname;
  history.replaceState(null, "", newUrl);
}

export type UseCompareShortlistReturn = {
  /** Slugs currently in the shortlist. */
  slugs: string[];
  /** True if the given slug is in the shortlist. */
  isAdded: (slug: string) => boolean;
  /** Toggle a provider into/out of the shortlist. */
  toggle: (slug: string) => void;
  /** Clear the entire shortlist. */
  clearAll: () => void;
  /** True if the shortlist is full (MAX_PROVIDERS reached). */
  isFull: boolean;
};

function readInitialSlugs(): string[] {
  // This runs only in the browser (lazy initializer is called after mount in
  // React 18 strict mode, but is still client-only when called from a hook
  // with 'use client').
  if (typeof window === "undefined") return [];
  const fromStorage = readFromStorage();
  const params = new URLSearchParams(window.location.search);
  const fromUrl =
    params
      .get(URL_PARAM)
      ?.split(",")
      .filter(Boolean)
      .slice(0, MAX_PROVIDERS) ?? [];
  // URL takes precedence; fall back to storage
  const initial = fromUrl.length > 0 ? fromUrl : fromStorage;
  // Sync storage with whatever we hydrated
  persistToStorage(initial);
  return initial;
}

export function useCompareShortlist(): UseCompareShortlistReturn {
  // Hydrate localStorage + URL after mount so the server and first client
  // render match when a page is opened with ?compare=slug1,slug2.
  const [slugs, setSlugs] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) {
        setSlugs(readInitialSlugs());
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const update = useCallback((next: string[]) => {
    setSlugs(next);
    persistToStorage(next);
    syncUrlParam(next);
  }, []);

  const isAdded = useCallback(
    (slug: string) => slugs.includes(slug),
    [slugs],
  );

  const toggle = useCallback(
    (slug: string) => {
      if (slugs.includes(slug)) {
        update(slugs.filter((s) => s !== slug));
      } else if (slugs.length < MAX_PROVIDERS) {
        update([...slugs, slug]);
      }
      // If full and not already in list, silently ignore
    },
    [slugs, update],
  );

  const clearAll = useCallback(() => {
    update([]);
  }, [update]);

  return {
    slugs,
    isAdded,
    toggle,
    clearAll,
    isFull: slugs.length >= MAX_PROVIDERS,
  };
}
